import { uniq, uniqWith } from 'es-toolkit';
import React from 'react';
import { Divider, Stack } from '@mui/material';
import { produce } from 'immer';

import { INDETERMINATE, isIndeterminate } from '@paragon/client-tools';
import { FeaturePatternTrussContainer } from '@paragon/contract-library';
import { distanceEquals, distanceLessThanOrEqual } from '@paragon/geometry-library';
import { PropertiesPanel } from '@paragon/layout-models';
import { DistanceField } from '@paragon/material';

import { TransformationButtons } from '../../../../library/transformations/controls/TransformationButtons';
import {
  editFeaturePatternContainers,
  FeaturePatternContainerPatchOrUpdateFn,
} from '../../../../store/actions/layout/featurePatternContainerActions';
import {
  featurePatternContainerElevationsMatch,
  getActiveLayoutLevelGuidOrNull,
  getFeaturePatternContainerElevationsFromActiveLevel,
} from '../../../../store/selectors/layoutDataSelectors';
import { isLayoutModeViewOnly } from '../../../../store/selectors/layoutModeSelectors';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { ProjectHierarchyContextSelects } from './ProjectHierarchyContextSelects';
import { PropertiesPanelMenuButton } from './PropertiesPanelMenuButton';
import { SyncElevationButton } from './SyncElevationButton';

/**
 * Note that we strongly desire to keep this panel as visually similar to the singular
 *   `FeaturePatternContainerProperties` panel as possible.  Please consider updating both panels when making any
 *    changes.
 */
export function BulkFeaturePatternContainerProperties(props: { containers: FeaturePatternTrussContainer[] }) {
  const dispatch = useAppDispatch();
  const viewOnlyProperties = useAppSelector(propertiesShouldBeViewOnly);
  const viewOnlyLayout = useAppSelector(isLayoutModeViewOnly);
  const viewOnly = viewOnlyProperties || viewOnlyLayout;

  const activeLevelGuid = useAppSelector(getActiveLayoutLevelGuidOrNull);
  const elevationsFromLevel = useAppSelector(getFeaturePatternContainerElevationsFromActiveLevel);

  const levelGuids = uniq(props.containers.map((container) => container.levelGuid));
  const commonLevelGuid = levelGuids.length === 1 ? levelGuids[0] : INDETERMINATE;

  const levelIsSuitableForSync =
    activeLevelGuid != null &&
    commonLevelGuid === activeLevelGuid &&
    elevationsFromLevel.topElevation != null &&
    elevationsFromLevel.bottomElevation != null;

  const topElevations = uniq(props.containers.map((container) => container.shape.topElevation));
  const commonTopElevation = topElevations.length === 1 ? topElevations[0] : INDETERMINATE;

  const bottomElevations = uniq(props.containers.map((container) => container.shape.bottomElevation));
  const commonBottomElevation = bottomElevations.length === 1 ? bottomElevations[0] : INDETERMINATE;

  const depths = uniqWith(
    props.containers.map((container) => container.shape.topElevation - container.shape.bottomElevation),
    distanceEquals,
  );
  const commonDepth = depths.length === 1 ? depths[0] : INDETERMINATE;

  function editAllContainers(changes: FeaturePatternContainerPatchOrUpdateFn) {
    return dispatch(
      editFeaturePatternContainers(
        props.containers.map((container) => ({
          guid: container.guid,
          changes,
        })),
      ),
    );
  }

  return (
    <PropertiesPanel
      title="Feature Pattern Containers"
      titleButton={<PropertiesPanelMenuButton type="featurePatternContainer" guid={props.containers[0].guid} />}
    >
      <Stack direction="column" spacing={2}>
        <TransformationButtons disabled={viewOnly} />

        <Divider />

        {!isIndeterminate(commonLevelGuid) && (
          <ProjectHierarchyContextSelects
            levelGuid={commonLevelGuid}
            onChange={(levelGuid) => editAllContainers({ levelGuid })}
            disabled={viewOnly}
          />
        )}

        {levelIsSuitableForSync && (
          <SyncElevationButton
            plural
            disabled={viewOnly}
            synchronized={
              typeof commonTopElevation === 'number' &&
              typeof commonBottomElevation === 'number' &&
              featurePatternContainerElevationsMatch(
                { shape: { topElevation: commonTopElevation, bottomElevation: commonBottomElevation } },
                elevationsFromLevel,
              )
            }
            onSynchronize={async () => {
              editAllContainers(
                produce((container) => {
                  Object.assign(container.shape, elevationsFromLevel);
                }),
              );
            }}
          />
        )}

        <Stack direction="row" spacing={1} sx={{ marginBottom: 1 }}>
          <DistanceField
            canBeIndeterminate
            label="Bottom Elevation"
            value={commonBottomElevation}
            validator={(bottomElevation) => {
              if (
                props.containers.some((container) =>
                  distanceLessThanOrEqual(container.shape.topElevation, bottomElevation),
                )
              ) {
                return 'Must be below top elevation';
              }
            }}
            onSubmit={async (bottomElevation) => {
              await editAllContainers(
                produce((container) => {
                  container.shape.bottomElevation = bottomElevation;
                }),
              );
            }}
            disabled={viewOnly}
          />

          <DistanceField
            canBeIndeterminate
            label="Depth"
            value={commonDepth}
            validator={(depth) => {
              if (distanceLessThanOrEqual(depth, 0)) {
                return 'Must be greater than 0';
              }
            }}
            onSubmit={async (depth) => {
              await editAllContainers(
                produce((container) => {
                  container.shape.topElevation = container.shape.bottomElevation + depth;
                }),
              );
            }}
            disabled={viewOnly}
          />

          <DistanceField
            canBeIndeterminate
            label="Top Elevation"
            value={commonTopElevation}
            validator={(topElevation) => {
              if (
                props.containers.some((container) =>
                  distanceLessThanOrEqual(topElevation, container.shape.bottomElevation),
                )
              ) {
                return 'Must be above bottom elevation';
              }
            }}
            onSubmit={async (topElevation) => {
              await editAllContainers(
                produce((container) => {
                  container.shape.topElevation = topElevation;
                }),
              );
            }}
            disabled={viewOnly}
          />
        </Stack>
      </Stack>
    </PropertiesPanel>
  );
}
